// src/pages/CookiePolicyPage.jsx - Enhanced Black & White Design
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Cookie, ArrowLeft, Mail, Globe, Shield, Settings } from 'lucide-react';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const CookiePolicyPage = () => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();

  // Scroll animations
  const [headerRef, headerVisible] = useScrollAnimation();
  const [contentRef, contentVisible] = useScrollAnimation();
  const [tableRef, tableVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();

  return (
    <div className="min-h-screen bg-white">
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-12">
        {/* Back Button - Enhanced */}
        <button
          onClick={() => navigate(-1)}
          className="group flex items-center gap-2 text-black/60 hover:text-black mb-8 transition-all duration-300 font-medium"
        >
          <ArrowLeft className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" />
          <span>{t('legal.back')}</span>
        </button>

        {/* Header - Enhanced */}
        <div
          ref={headerRef}
          className={`
            relative bg-white rounded-2xl border-[3px] border-black shadow-2xl p-8 mb-8 overflow-hidden
            transition-all duration-1000
            ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
          `}
        >
          {/* Decorative corners */}
          <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-black/10 rounded-tl-2xl" />
          <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-black/10 rounded-br-2xl" />

          <div className="relative z-10 flex items-start gap-6">
            {/* Icon */}
            <div className="flex-shrink-0 w-16 h-16 bg-black rounded-2xl flex items-center justify-center shadow-lg">
              <Cookie className="w-8 h-8 text-white" />
            </div>

            {/* Title */}
            <div className="flex-1">
              <h1 className="font-space text-3xl sm:text-4xl font-black text-black mb-2 tracking-tight">
                {t('legal.cookies.title')}
              </h1>
              <p className="text-black/60 font-medium">
                {t('legal.lastUpdated')}: October 12, 2025
              </p>
              <p className="text-black/60 text-sm mt-2">
                {t('footer.disclaimer')}
              </p>
            </div>
          </div>
        </div>

        {/* Content - Enhanced */}
        <div
          ref={contentRef}
          className="relative bg-white rounded-2xl border-[3px] border-black shadow-2xl p-8 mb-8 overflow-hidden"
        >
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50 opacity-50" />

          <div className="relative z-10 prose prose-lg max-w-none">
            {/* Introduction */}
            <section
              className={`
                mb-10 pb-8 border-b border-black/10
                transition-all duration-700
                ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
              `}
            >
              <h2 className="font-space text-2xl font-black text-black tracking-tight mb-4">
                {t('legal.cookies.intro.title')}
              </h2>
              <p className="text-black/70 leading-relaxed">
                {t('legal.cookies.intro.text')}
              </p>
            </section>

            {/* 1. What Are Cookies */}
            <section
              className={`
                mb-10 pb-8 border-b border-black/10
                transition-all duration-700 delay-100
                ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
              `}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center font-bold">
                  1
                </div>
                <h2 className="font-space text-2xl font-black text-black tracking-tight flex-1">
                  {t('legal.cookies.what.title')}
                </h2>
              </div>
              <p className="text-black/70 leading-relaxed">
                {t('legal.cookies.what.text')}
              </p>
            </section>

            {/* 2. Types of Cookies We Use */}
            <section
              className={`
                mb-10 pb-8 border-b border-black/10
                transition-all duration-700 delay-200
                ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
              `}
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0 w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center font-bold">
                  2
                </div>
                <h2 className="font-space text-2xl font-black text-black tracking-tight flex-1">
                  {t('legal.cookies.types.title')}
                </h2>
              </div>

              {/* Essential Cookies */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-black mb-3 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  {t('legal.cookies.types.essential.title')}
                </h3>
                <p className="text-black/70 leading-relaxed mb-3">
                  {t('legal.cookies.types.essential.text')}
                </p>
                <ul className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <li key={i} className="flex items-start gap-3 group">
                      <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                        <span className="text-xs font-bold">✓</span>
                      </div>
                      <span className="text-black/70 leading-relaxed flex-1">
                        {t(`legal.cookies.types.essential.item${i}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Functional Cookies */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-black mb-3 flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  {t('legal.cookies.types.functional.title')}
                </h3>
                <p className="text-black/70 leading-relaxed mb-3">
                  {t('legal.cookies.types.functional.text')}
                </p>
                <ul className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <li key={i} className="flex items-start gap-3 group">
                      <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                        <span className="text-xs font-bold">✓</span>
                      </div>
                      <span className="text-black/70 leading-relaxed flex-1">
                        {t(`legal.cookies.types.functional.item${i}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Analytics Cookies */}
              <div>
                <h3 className="text-xl font-bold text-black mb-3 flex items-center gap-2">
                  <Cookie className="w-5 h-5" />
                  {t('legal.cookies.types.analytics.title')}
                </h3>
                <p className="text-black/70 leading-relaxed mb-3">
                  {t('legal.cookies.types.analytics.text')}
                </p>
                <ul className="space-y-3">
                  {[1, 2].map(i => (
                    <li key={i} className="flex items-start gap-3 group">
                      <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                        <span className="text-xs font-bold">✓</span>
                      </div>
                      <span className="text-black/70 leading-relaxed flex-1">
                        {t(`legal.cookies.types.analytics.item${i}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* 3-7: Remaining sections */}
            {[
              { num: 3, key: 'thirdParty', special: true },
              { num: 4, key: 'control', items: 3 },
              { num: 5, key: 'browser', browserList: true },
              { num: 6, key: 'localStorage', items: 0 },
              { num: 7, key: 'updates', items: 0 }
            ].map((section, idx) => (
              <section
                key={section.key}
                className={`
                  mb-10 pb-8 border-b border-black/10
                  transition-all duration-700
                  ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
                `}
                style={{ transitionDelay: contentVisible ? `${(idx + 3) * 0.1}s` : '0s' }}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center font-bold">
                    {section.num}
                  </div>
                  <h2 className="font-space text-2xl font-black text-black tracking-tight flex-1">
                    {t(`legal.cookies.${section.key}.title`)}
                  </h2>
                </div>

                <p className="text-black/70 leading-relaxed mb-4">
                  {t(`legal.cookies.${section.key}.text`)}
                </p>

                {section.special && (
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 group">
                      <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                        <span className="text-xs font-bold">✓</span>
                      </div>
                      <span className="text-black/70 leading-relaxed flex-1">
                        <strong className="text-black">Firebase:</strong> {t('legal.cookies.thirdParty.firebase')}
                      </span>
                    </li>
                    <li className="flex items-start gap-3 group">
                      <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                        <span className="text-xs font-bold">✓</span>
                      </div>
                      <span className="text-black/70 leading-relaxed flex-1">
                        <strong className="text-black">Google Analytics:</strong> {t('legal.cookies.thirdParty.analytics')}
                      </span>
                    </li>
                  </ul>
                )}

                {section.items > 0 && (
                  <ul className="space-y-3">
                    {[...Array(section.items)].map((_, i) => (
                      <li key={i} className="flex items-start gap-3 group">
                        <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                          <span className="text-xs font-bold">✓</span>
                        </div>
                        <span className="text-black/70 leading-relaxed flex-1">
                          {t(`legal.cookies.${section.key}.item${i + 1}`)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.browserList && (
                  <ul className="space-y-3">
                    {[
                      { name: 'Chrome', path: 'Settings → Privacy and Security → Cookies' },
                      { name: 'Firefox', path: 'Settings → Privacy & Security → Cookies' },
                      { name: 'Safari', path: 'Preferences → Privacy → Cookies' },
                      { name: 'Edge', path: 'Settings → Cookies and Site Permissions' }
                    ].map((browser) => (
                      <li key={browser.name} className="flex items-start gap-3 group">
                        <div className="flex-shrink-0 w-6 h-6 bg-black/5 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                          <span className="text-xs font-bold">→</span>
                        </div>
                        <span className="text-black/70 leading-relaxed flex-1">
                          <strong className="text-black">{browser.name}:</strong> {browser.path}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            {/* 8. Contact */}
            <section
              className={`
                transition-all duration-700 delay-800
                ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
              `}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center font-bold">
                  8
                </div>
                <h2 className="font-space text-2xl font-black text-black tracking-tight flex-1">
                  {t('legal.cookies.contact.title')}
                </h2>
              </div>

              <p className="text-black/70 leading-relaxed mb-4">
                {t('legal.cookies.contact.text')}
              </p>

              <div className="relative bg-white border-2 border-black/10 rounded-xl p-6 group hover:border-black transition-all duration-300">
                <p className="font-bold text-black mb-4 text-lg">Logami</p>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-black/70">
                    <div className="w-8 h-8 bg-black/5 rounded-lg flex items-center justify-center group-hover:bg-black group-hover:text-white transition-all duration-300">
                      <Mail className="w-4 h-4" />
                    </div>
                    <span className="font-medium">team@logami.app</span>
                  </div>

                  <div className="flex items-center gap-3 text-black/70">
                    <div className="w-8 h-8 bg-black/5 rounded-lg flex items-center justify-center group-hover:bg-black group-hover:text-white transition-all duration-300">
                      <Globe className="w-4 h-4" />
                    </div>
                    <span className="font-medium">https://logami.app</span>
                  </div>
                </div>

                <div className="absolute top-3 right-3 w-3 h-3 bg-black/10 rounded-full group-hover:bg-black transition-colors duration-300" />
              </div>
            </section>
          </div>
        </div>

        {/* Cookie Table - Enhanced */}
        <div
          ref={tableRef}
          className={`
            relative bg-white rounded-2xl border-[3px] border-black shadow-2xl p-8 mb-8 overflow-hidden
            transition-all duration-1000
            ${tableVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
          `}
        >
          {/* Decorative corners */}
          <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-black/10 rounded-tl-2xl" />
          <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-black/10 rounded-br-2xl" />

          <div className="relative z-10">
            <h2 className="font-space text-2xl font-black text-black mb-6 tracking-tight">
              {t('legal.cookies.table.title')}
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="py-4 px-4 font-space font-black text-black">{t('legal.cookies.table.name')}</th>
                    <th className="py-4 px-4 font-space font-black text-black">{t('legal.cookies.table.purpose')}</th>
                    <th className="py-4 px-4 font-space font-black text-black">{t('legal.cookies.table.duration')}</th>
                    <th className="py-4 px-4 font-space font-black text-black">{t('legal.cookies.table.type')}</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      name: 'firebase_auth',
                      purpose: t('legal.cookies.table.firebase'),
                      duration: t('legal.cookies.table.session'),
                      type: t('legal.cookies.table.essential'),
                      typeColor: 'bg-black text-white'
                    },
                    {
                      name: 'i18nextLng',
                      purpose: t('legal.cookies.table.language'),
                      duration: t('legal.cookies.table.persistent'),
                      type: t('legal.cookies.table.functional'),
                      typeColor: 'bg-black/10 text-black'
                    },
                    {
                      name: 'user_preferences',
                      purpose: t('legal.cookies.table.preferences'),
                      duration: t('legal.cookies.table.persistent'),
                      type: t('legal.cookies.table.functional'),
                      typeColor: 'bg-black/10 text-black'
                    }
                  ].map((cookie, index) => (
                    <tr
                      key={cookie.name}
                      className={`
                        border-b border-black/10 group hover:bg-black/5 transition-colors duration-300
                        ${tableVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
                      `}
                      style={{ transitionDelay: tableVisible ? `${index * 0.1}s` : '0s' }}
                    >
                      <td className="py-4 px-4 font-mono text-sm font-bold text-black">{cookie.name}</td>
                      <td className="py-4 px-4 text-black/70">{cookie.purpose}</td>
                      <td className="py-4 px-4 text-black/70">{cookie.duration}</td>
                      <td className="py-4 px-4">
                        <span className={`${cookie.typeColor} px-3 py-1 rounded-lg text-xs font-bold`}>
                          {cookie.type}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer CTA - Enhanced */}
        <div
          ref={ctaRef}
          className={`
            relative bg-white border-[3px] border-black rounded-2xl p-8 text-center overflow-hidden group
            transition-all duration-1000 hover:shadow-2xl
            ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
          `}
        >
          <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-black/10 rounded-tl-2xl" />
          <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-black/10 rounded-br-2xl" />

          <div className="relative z-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-black rounded-2xl mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
              <Cookie className="w-8 h-8 text-white" />
            </div>

            <p className="text-lg text-black/70 mb-6 max-w-2xl mx-auto leading-relaxed">
              {t('legal.cookies.footer.text')}
            </p>

            <button
              onClick={() => navigate('/settings')}
              className="group/btn relative px-8 py-4 bg-black text-white font-space font-bold rounded-xl border-3 border-black overflow-hidden transition-all duration-500 hover:scale-105 hover:shadow-2xl"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-gray-800 via-black to-gray-800 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500" style={{
                backgroundSize: '200% 100%',
                animation: 'shimmer 3s infinite'
              }} />

              <span className="relative z-10 flex items-center justify-center gap-2">
                <Settings className="w-5 h-5 transition-transform duration-300 group-hover/btn:rotate-90" />
                <span>{t('legal.cookies.footer.button')}</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;